import LocalScene, {
  GeocentricCoordinate,
  ProjectedCoordinate,
} from '../projections';
import {
  AerialAreaMeasurement,
  AerialMeasurement,
  Measurement3D,
  MeasurementArea,
  MeasurementPoint,
  MeasurementVolume,
  MeasurementController
} from '.';
import {dialog, fs, isStaticSite} from "../../../electron-modules";
import slash from 'slash';
import { Vector3 } from 'three';
import { windowSafeDateISO } from '../utilities';
import {
  measureCSVFilter,
  measureDataFilter,
  measureDataFormat
} from '../../../file-extensions';
import { t } from '../../../localization';
import { toast } from '../../../app';

export class MeasurementGroupExporter {
  private controller: MeasurementController;
  private lineDelimiter = "\n";
  private columnDelimiter = ',';

  constructor(controller) {
    this.controller = controller;
  }

  get viewer() {
    return this.controller.viewer;
  }

  get formatter() {
    return this.controller.formatter;
  }

  exportLinework(measurementGroup, type, version) {
    //let lines = measurementGroup.getLineCoordinates();
    //let points = measurementGroup.getPointCoordinates();
    //let name = `${measurementGroup.name} (${type}).zip`;

    // Need to implement dxf/shp javascript write
  }

  async exportCSV(measurementGroup, ordering) {
    const precision = this.formatter.getVectorPrecision(LocalScene.viewProjection);
    await this.export3D(measurementGroup, this.formatter, ordering, precision);
    await this.export2D(measurementGroup, this.formatter, ordering, precision);
    toast.success(t('measure.measurements_exported_successfully'));
  }

  async exportDAT(measurementGroup) {
    let data = {
      count: 0,
      name: measurementGroup.name,
      projection: LocalScene.dataProjection,
      measurements: {
        measurements2D: [],
        measurements3D: []
      }
    };

    let measurements = measurementGroup.measurements;
    measurements.forEach(measurement => {
      const created = measurement.timeCreated
        .toISOString()
        .split('.')[0];

      const info = {
        id: null,
        coordinates: measurement.coordinates,
        time_created: created,
        title: measurement.title,
        type: measurement.type,
        volume: null,
      };

      if (measurement instanceof AerialMeasurement) {
        data.measurements.measurements2D.push(info);
      } else {
        if (measurement instanceof MeasurementVolume) {
          info.volume = measurement.volumeHelper.value;
        }

        data.measurements.measurements3D.push(info);
      }

      data.count += 1;
    });

    // Download data export file
    const name = this.viewer.projectName;
    await this.downloadDataExport(name, data);
    toast.success(t('measure.measurements_exported_successfully'));
  }

  async export2D(measurementGroup, formatter, ordering, precision) {
    let measurements = measurementGroup.measurements.filter(measurement =>
      measurement instanceof AerialMeasurement
    );

    if (measurements.length === 0) {
      return;
    }

    let mapProjection = this.viewer.minimap.mapSystem;

    let header = this.getHeaderValues2D(measurements, ordering, formatter);
    let nullValue = formatter.nullValue;

    let csv = [
      header.join(this.columnDelimiter)
    ];

    // Load measurements in reverse order
    measurements.reverse();
    measurements.forEach(measurement => {
      let row;
      let value;

      if (measurement instanceof AerialAreaMeasurement) {
        value = formatter.convertUnits(measurement.area2d, measurement.type);
        row = [measurement.title, t('measure.area'), nullValue, value];
      } else {
        value = formatter.convertUnits(measurement.distance2D, measurement.type);
        row = [measurement.title, t('measure.length'), value, nullValue];
      }

      let coordinates = measurement.geometry.flatCoordinates;
      if (measurement instanceof AerialAreaMeasurement) {
        coordinates = coordinates.slice(0, coordinates.length - 2);
      }

      let vertices = [];
      let numPoints = coordinates.length / 2.0;

      for (var i = 0; i < numPoints; i++) {
        let x = coordinates[2 * i];
        let y = coordinates[(2 * i) + 1];

        let coord = new Vector3(x, y, 0.0);
        let ecef = new ProjectedCoordinate(coord, mapProjection).toGeocentric();
        let proj = new GeocentricCoordinate(ecef).toViewProjection();
        vertices.push(proj);
      }

      vertices.forEach(vertex => {
        // Export with x and y values swapped
        if (ordering === "yxz") {
          vertex = new Vector3(vertex.y, vertex.x, vertex.z);
        }

        row = row.concat([
          vertex.x.toFixed(precision.x),
          vertex.y.toFixed(precision.y),
        ]);
      });

      csv.push(row.join(this.columnDelimiter));
    });

    await this.downloadCSVFile(t('measure.name-measurements-2d', {
      name: this.viewer.projectName,
      date: windowSafeDateISO()
    }), csv);
  }

  async export3D(measurementGroup, formatter, ordering, precision) {
    let measurements = measurementGroup.measurements.filter(measurement =>
      measurement instanceof Measurement3D
    );

    if (measurements.length === 0) {
      return;
    }

    let header = this.getHeaderValues3D(measurements, ordering, formatter);
    let nullValue = formatter.nullValue;

    let csv = [
      header.join(this.columnDelimiter)
    ];

    // Load measurements in reverse order
    measurements.reverse();
    measurements.forEach(measurement => {
      let row;

      if (measurement instanceof MeasurementPoint) {
        // Most data will be blank for a single point
        row = [
          measurement.title,
          nullValue,
          nullValue,
          nullValue,
          nullValue,
          nullValue,
          nullValue
        ];
      } else if (measurement instanceof MeasurementArea) {
        let areaValue;
        let volumeValue = nullValue;

        if (measurement.valid) {
          areaValue = formatter.convertUnits(measurement.area3D, measurement.type);
        } else {
          areaValue = measurement.areaInvalidMessage;
        }

        if (measurement instanceof MeasurementVolume) {
          volumeValue = measurement.invalidVolumeMessage;
          if (measurement.valid && measurement.calculated) {
            volumeValue = formatter.convertUnits(measurement.volume, measurement.type);
          }
        }

        row = [
          measurement.title,
          nullValue,
          nullValue,
          areaValue,
          volumeValue,
          nullValue,
          nullValue
        ];
      } else {
        row = [
          measurement.title,
          formatter.convertUnits(measurement.distance2D, measurement.type),
          formatter.convertUnits(measurement.distance3D, measurement.type),
          nullValue,
          nullValue,
          formatter.formatGrade(measurement.grade, false),
          formatter.formatAngle(measurement.angle, false)
        ];
      }

      let verticesReprojected = measurement.vertices.map(vertex => {
        return vertex.toViewProjection();
      });

      verticesReprojected.forEach(vertex => {
        // Export with x and y values swapped
        if (ordering === "yxz") {
          vertex = new Vector3(vertex.y, vertex.x, vertex.z);
        }

        row = row.concat([
          vertex.x.toFixed(precision.x),
          vertex.y.toFixed(precision.y),
          vertex.z.toFixed(precision.z)
        ]);
      });

      csv.push(row.join(this.columnDelimiter));
    });

    await this.downloadCSVFile(t('measure.name-measurements-3d', {
      name: this.viewer.projectName,
      date: windowSafeDateISO()
    }), csv);
  }

  async downloadDataExport(filename, data) {
    let name = `${filename}.${measureDataFormat}`;
    let string = JSON.stringify(data, null, 2);

    if (isStaticSite) {
      this.downloadFileBrowser(name, string, "plain");
    } else {
      await this.downloadFileElectron(name, string, measureDataFilter);
    }
  }

  async downloadCSVFile(filename, data) {
    let name = `${filename}.csv`;
    let string = data.join(this.lineDelimiter);

    if (isStaticSite) {
      this.downloadFileBrowser(name, string, "csv");
    } else {
      await this.downloadFileElectron(name, string, measureCSVFilter);
    }
  }

  async downloadFileElectron(name, data, filter) {
    const result = await dialog.showSaveDialog({
      title: t("dialog.save-project"),
      defaultPath: name,
      filters: filter,
    });

    const {filePath, canceled} = result;
    if (canceled || !filePath) return;

    const savePath = slash(filePath);
    fs.writeFileSync(savePath, data);
  }

  downloadFileBrowser(name, data, type) {
    let contentType = `data:text/${type};charset=utf-8`;
    let content = `${contentType},${encodeURIComponent(data)}`;

    let element = document.createElement('a');
    element.setAttribute('href', content);
    if (name) {
      element.setAttribute('download', name);
    }
    element.style.display = 'none';
    document.body.appendChild(element);
    element.target = "_blank";
    element.click();
    document.body.removeChild(element);
  }

  getHeaderValues2D(measurements, ordering, formatter) {
    let header = [
      t('measure.notes'),
      t('measure.measure-type'),
      t('measure.length-formatter-units', {units: formatter.units}),
      t('measure.area-formatter-units-2', {units: formatter.units})
    ];

    let numVertices = 0;
    measurements.forEach(measurement => {
      let coordinates = measurement.geometry.flatCoordinates;
      if (measurement instanceof AerialAreaMeasurement) {
        coordinates = coordinates.slice(0, coordinates.length - 2);
      }

      let numPoints = coordinates.length / 2.0;
      numVertices = Math.max(numVertices, numPoints);
    });

    for (var i = 0; i < numVertices; i++) {
      if (ordering === "yxz") {
        header = header.concat([`Y_${i + 1}`, `X_${i + 1}`]);
      } else {
        header = header.concat([`X_${i + 1}`, `Y_${i + 1}`]);
      }
    }

    return header;
  }

  getHeaderValues3D(measurements, ordering, formatter) {
    let header = [
      t('measure.notes'),
      t('measure.distance-2d-formatter-units', {units: formatter.units}),
      t('measure.distance-3d-formatter-units', {units: formatter.units}),
      t('measure.area-3d-formatter-areaunits', {units: formatter.areaUnits}),
      t('measure.volume-3d-formatter-volumeunits', {units: formatter.volumeUnits}),
      t('measure.grade'),
      t('measure.angle-deg'),
    ];

    // Determine the max number of vertices
    let numVertices = 0;
    measurements.forEach(measurement => {
      let measurementVertices = measurement.vertices.length;
      numVertices = Math.max(numVertices, measurementVertices);
    });

    for (var i = 0; i < numVertices; i++) {
      if (ordering === "yxz") {
        header = header.concat([`Y_${i + 1}`, `X_${i + 1}`, `Z_${i + 1}`]);
      } else {
        header = header.concat([`X_${i + 1}`, `Y_${i + 1}`, `Z_${i + 1}`]);
      }
    }

    return header;
  }
}
